/**
 * The studio's karkhana looms for the product list filter and the product's loom picker. Kept for a few minutes
 * per signed-in user (a new login never sees the last studio's looms); a studio without Karkhana gets none, and
 * the screens keep plain text.
 */
import { useEffect, useState } from "react";
import { listLooms } from "../../../services/karkhanaApi";

export type StudioLoom = { id: string; code: string; previousCodes?: string[]; retired?: boolean };

/** A loom added or renamed elsewhere shows within this long. */
const KEEP_MS = 5 * 60 * 1000;

let cached: { token: string; at: number; looms: Promise<StudioLoom[]> } | null = null;

const currentToken = (): string => {
  try {
    return localStorage.getItem("token") || "";
  } catch {
    return "";
  }
};

export const loadStudioLooms = (now = Date.now()): Promise<StudioLoom[]> => {
  const token = currentToken();
  if (!token) return Promise.resolve([]);
  if (cached && cached.token === token && now - cached.at < KEEP_MS) return cached.looms;
  const entry = {
    token,
    at: now,
    looms: Promise.resolve(listLooms())
      .then((res: any) => {
        // A failed reply is not kept, so the next screen asks again.
        if (!res?.success || !Array.isArray(res.data)) throw new Error("looms unavailable");
        return res.data as any[];
      })
      .then((rows) =>
        rows
          .filter((l) => l && l.id && l.code)
          .map((l) => ({ id: String(l.id), code: String(l.code), previousCodes: l.previousCodes || [], retired: Boolean(l.retired) }))
          .sort(byCode),
      )
      .catch(() => {
        if (cached === entry) cached = null;
        return [] as StudioLoom[];
      }),
  };
  cached = entry;
  return entry.looms;
};

/** For tests and sign-out: forget the looms read so far. */
export const forgetStudioLooms = () => {
  cached = null;
};

/**
 * Same key as the server's loom codes (KarkhanaLoomRegistry.codeKey): case, spaces and _ - . : / ignored, and the
 * zeros after the letters dropped, so B01 = B1 = b-1. Plain numbers keep their zeros (01 and 1 are two looms).
 */
export const loomCodeKey = (code: unknown): string =>
  String(code ?? "")
    .normalize("NFKC")
    .toUpperCase()
    .replace(/[\s_\-.:/]/g, "")
    .replace(/([A-Z])0+(?=\d)/g, "$1");

/** Natural order: N2 before N10, retired looms last. */
export const byCode = (a: StudioLoom, b: StudioLoom): number =>
  Number(Boolean(a.retired)) - Number(Boolean(b.retired)) || a.code.localeCompare(b.code, undefined, { numeric: true, sensitivity: "base" });

/** The studio loom a typed number means: its code or a code it had before a rename (B1 = B01). */
export const loomFor = (looms: StudioLoom[], typed: unknown): StudioLoom | undefined => {
  const key = loomCodeKey(typed);
  if (!key) return undefined;
  const current = looms.filter((l) => loomCodeKey(l.code) === key);
  if (current.length) return current.length === 1 ? current[0] : undefined;
  const before = looms.filter((l) => (l.previousCodes || []).some((c) => loomCodeKey(c) === key));
  return before.length === 1 ? before[0] : undefined;
};

/** undefined while loading, then the looms (empty when the studio has none). */
export const useStudioLooms = (): StudioLoom[] | undefined => {
  const [looms, setLooms] = useState<StudioLoom[] | undefined>(undefined);
  useEffect(() => {
    let live = true;
    loadStudioLooms().then((rows) => live && setLooms(rows));
    return () => {
      live = false;
    };
  }, []);
  return looms;
};
